import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Camera, Check, RefreshCw, Upload } from "lucide-react";
import { API } from "../auth/AuthContext";
import { bindStreamToVideo, formatCameraError, requestCameraStream } from "../lib/cameraStream";
import { compressImageForUpload } from "../lib/compressImage";

const ALLOWED_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

function scanErrorMessage(detail) {
  if (detail === "file_too_large") return "That image is too large. Move closer and retake it.";
  if (detail === "image_dimensions_too_large") return "That image resolution is too large. Use a smaller photo.";
  if (detail === "unsupported_file_type") return "Use a JPEG, PNG, or WEBP image.";
  if (detail === "content_type_mismatch") return "The image type does not match the file. Use a different photo.";
  if (detail === "invalid_image") return "That file is not a readable image.";
  if (detail === "rate_limited") return "Too many scan attempts. Wait a minute or enter the VIN manually.";
  if (detail === "vin_scan_unreadable") return "We could not read one clear VIN. Retake the photo or enter it manually.";
  if (detail === "vin_scan_unavailable") return "VIN scanning is temporarily unavailable. Enter the VIN manually.";
  return "We could not scan that image. Retake it or enter the VIN manually.";
}

export default function VinScanner({ onConfirm, testid = "vin-scanner" }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const abortRef = useRef(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [candidate, setCandidate] = useState(null);

  const stopCamera = () => {
    abortRef.current?.abort();
    abortRef.current = null;
    streamRef.current?.getTracks?.().forEach((track) => track.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraOpen(false);
  };

  useEffect(() => () => {
    abortRef.current?.abort();
    streamRef.current?.getTracks?.().forEach((track) => track.stop());
  }, []);

  const openCamera = async () => {
    setError("");
    setCandidate(null);
    setCameraOpen(true);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const stream = await requestCameraStream({ signal: controller.signal });
      streamRef.current = stream;
      // Allow the conditional <video> to mount when getUserMedia resolves immediately.
      await new Promise((resolve) => setTimeout(resolve, 0));
      const ready = await bindStreamToVideo(videoRef.current, stream);
      if (!ready) throw new Error("Camera opened but no image was available.");
    } catch (err) {
      if (err?.name !== "AbortError") setError(formatCameraError(err));
      stopCamera();
    }
  };

  const scanFile = async (file) => {
    setError("");
    setCandidate(null);
    if (!file || !ALLOWED_TYPES.has(file.type)) {
      setError("Use a JPEG, PNG, or WEBP image.");
      return;
    }
    setBusy(true);
    try {
      const prepared = await compressImageForUpload(file, {
        maxBytes: 4 * 1024 * 1024,
        maxEdge: 3000,
      });
      const body = new FormData();
      body.append("image", prepared, prepared.name || "vin.jpg");
      const response = await axios.post(`${API}/vehicles/vin/scan`, body, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (response.data?.status !== "candidate" || !response.data?.vin) {
        setError("We could not read one clear VIN. Retake the photo or enter it manually.");
        return;
      }
      setCandidate(response.data);
      stopCamera();
    } catch (err) {
      setError(scanErrorMessage(err.response?.data?.detail || err.message));
    } finally {
      setBusy(false);
    }
  };

  const capture = async () => {
    const video = videoRef.current;
    if (!video?.videoWidth || !video?.videoHeight) {
      setError("The camera is not ready. Try again or upload a photo.");
      return;
    }
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d")?.drawImage(video, 0, 0);
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          setError("Could not capture the photo. Try upload instead.");
          return;
        }
        scanFile(new File([blob], "vin-camera.jpg", { type: "image/jpeg" }));
      },
      "image/jpeg",
      0.92,
    );
  };

  const confirmCandidate = () => {
    if (!candidate?.vin) return;
    onConfirm(candidate.vin);
    setCandidate(null);
  };

  return (
    <div className="mt-4 rounded-xl border border-[var(--zc-border)] bg-[var(--zc-bg-3)] p-4" data-testid={testid}>
      <div className="flex flex-wrap gap-2">
        <button type="button" className="zc-btn-outline-secondary h-10 px-4 text-xs inline-flex items-center gap-2" onClick={openCamera} disabled={busy}>
          <Camera size={14} /> Scan with camera
        </button>
        <label className="zc-btn-ghost h-10 px-4 text-xs inline-flex items-center gap-2 cursor-pointer">
          <Upload size={14} /> Upload VIN photo
          <input
            type="file"
            className="sr-only"
            accept="image/jpeg,image/png,image/webp"
            aria-label="Upload VIN photo"
            disabled={busy}
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = "";
              if (file) scanFile(file);
            }}
          />
        </label>
      </div>

      {cameraOpen && (
        <div className="mt-4">
          <video ref={videoRef} muted playsInline className="w-full max-h-72 rounded-xl bg-[var(--zc-text)] object-cover" aria-label="VIN camera preview" />
          <div className="flex flex-wrap gap-2 mt-3">
            <button type="button" className="zc-btn-primary h-10 px-4 text-xs inline-flex items-center gap-2" onClick={capture} disabled={busy}>
              <Camera size={14} /> {busy ? "Reading…" : "Capture VIN"}
            </button>
            <button type="button" className="zc-btn-ghost h-10 px-4 text-xs" onClick={stopCamera}>Cancel camera</button>
          </div>
        </div>
      )}

      {busy && <p className="zc-meta mt-3" role="status">Reading the VIN securely…</p>}
      {error && <p className="text-xs text-[var(--zc-danger)] mt-3" role="alert">{error}</p>}

      {candidate && (
        <div className="mt-4 rounded-xl border border-[var(--zc-border-2)] bg-[var(--zc-bg-2)] p-4" data-testid={`${testid}-candidate`}>
          <p className="text-xs font-semibold text-[var(--zc-text)]">Review the scanned VIN</p>
          <p className="font-mono tracking-wide text-base mt-2 break-all">{candidate.vin}</p>
          <p className="zc-meta mt-2">Compare every character with the vehicle. The VIN is not added until you confirm it.</p>
          <div className="flex flex-wrap gap-2 mt-3">
            <button type="button" className="zc-btn-primary h-10 px-4 text-xs inline-flex items-center gap-2" onClick={confirmCandidate}>
              <Check size={14} /> Confirm this VIN
            </button>
            <button type="button" className="zc-btn-ghost h-10 px-4 text-xs inline-flex items-center gap-2" onClick={() => setCandidate(null)}>
              <RefreshCw size={14} /> Retake
            </button>
          </div>
        </div>
      )}

      <p className="zc-meta mt-3">Your photo is processed temporarily and is not saved. You can always enter the VIN manually above.</p>
    </div>
  );
}

